import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";
import { actionButtonsPropsZ } from "@components/Chat/JsonRenderComponents/chatActions";
import { LAYOUT_CATALOG_COMPONENTS } from "../../JsonRenderComponents/layout/layoutCatalog";

const jsonRenderAssetTypeZ = z.enum(["stock", "crypto"]);

const signalCardAssetPropsZ = {
  id: z.string().optional(),
  assetLogo: z.string().optional(),
  assetSymbol: z.string(),
  type: jsonRenderAssetTypeZ,
};

const chartAnnotationZ = z.object({
  pointIndex: z.number().int().nonnegative(),
  label: z.string(),
});

const ohlcCandleZ = z.object({
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
});

const earningsMetricZ = z.object({
  metricKey: z.string(),
  actualValue: z.string(),
  expectedValue: z.string(),
  result: z.enum(["beat", "miss", "inline"]),
});

const priceChangeDirectionZ = z.enum(["up", "down", "flat"]);

const fitnessScoreDimensionSchema = z.object({
  score: z.number(),
  delta: z.number(),
});

export const Catalog = defineCatalog(schema, {
  components: {
    ...LAYOUT_CATALOG_COMPONENTS,
    Button: {
      props: z.object({
        label: z.string(),
        action: z.string(),
      }),
      description: "Clickable button",
    },
    NotificationText: {
      props: z.object({
        text: z.string(),
        className: z.string().optional(),
      }),
      description:
        "Notification body: `text` is Markdown (shared Markdown component, GFM).",
    },
    NotificationCardContainer: {
      props: z.object({
        label: z.string(),
        title: z.string(),
        generatedAtMs: z.number().int(),
        summary: z.string().optional(),
        className: z.string().optional(),
        metrics: z.record(z.string(), z.unknown()).optional(),
        source: z
          .object({
            ticker: z.array(z.string()).optional(),
            theme: z.array(z.string()).optional(),
          })
          .optional(),
        eventType: z.string().optional(),
        business: z
          .object({
            type: z.string(),
            id: z.string(),
          })
          .optional(),
      }),
      description: "Display a notification card container",
    },
    AssetRecommendationList: {
      props: z.object({
        sectionLabel: z.string().optional(),
        assets: z.array(
          z.object({
            id: z.string().optional(),
            assetLogo: z.string().optional(),
            assetSymbol: z.string(),
            assetPriceUsd: z.number(),
            priceChange24h: z.number(),
            reason: z.string(),
            assetScoreValue: z.number(),
            assetScoreTotal: z.number(),
            type: jsonRenderAssetTypeZ,
          }),
        ),
        className: z.string().optional(),
        listClassName: z.string().optional(),
      }),
      description: "Display a asset recommendation list",
    },
    AssetHeader: {
      props: z.object({
        id: z.string().optional(),
        assetLogo: z.string().optional(),
        assetSymbol: z.string(),
        title: z.string(),
        assetScoreValue: z.number().optional(),
        assetScoreTotal: z.number().optional(),
        type: jsonRenderAssetTypeZ,
      }),
      description: "Display a asset header",
    },
    MultiAssetDimensionScoreComparisonTable: {
      props: z.object({
        dimensions: z.array(
          z.object({
            dimensionKey: z.string(),
            dimensionName: z.string(),
          }),
        ),
        assets: z.array(
          z.object({
            id: z.string().optional(),
            assetLogo: z.string().optional(),
            assetSymbol: z.string(),
            assetName: z.string(),
            type: jsonRenderAssetTypeZ,
            dimensionScores: z.array(
              z.object({
                dimensionKey: z.string(),
                scoreValue: z.number(),
                scoreTotal: z.number(),
                rankValue: z.number(),
                rankTotalAssets: z.number(),
              }),
            ),
          }),
        ),
        sectionLabel: z.string().optional(),
      }),
      description: "Display a multi asset dimension score comparison table",
    },
    AssetDimensionScoreTable: {
      props: z.object({
        rows: z.array(
          z.object({
            dimensionName: z.string(),
            scoreValue: z.number(),
            scoreTotal: z.number(),
            rankValue: z.number(),
            rankTotalAssets: z.number(),
          }),
        ),
        sectionLabel: z.string().optional(),
      }),
      description: "Display a asset dimension score table",
    },
    AssetDataGridTable: {
      props: z.object({
        tableColumns: z.array(
          z.object({
            columnKey: z.string(),
            columnTitle: z.string(),
            columnStyle: z.enum(["default", "badge", "accent"]).optional(),
          }),
        ),
        tableRows: z.array(
          z.object({
            rowKey: z.string(),
            rowName: z.string(),
            cells: z.array(
              z.object({
                columnKey: z.string(),
                cellValue: z.string(),
              }),
            ),
          }),
        ),
        firstColumnHeader: z.string().optional(),
      }),
      description: "Display a asset data grid table",
    },
    AssetKeyStatsStrip: {
      props: z.object({
        items: z.array(
          z.object({
            metricKey: z.string(),
            metricName: z.string(),
            metricValue: z.string(),
            metricChange: z.string(),
          }),
        ),
      }),
      description: "Display a asset key stats strip",
    },
    AssetDimensionRankChangeTable: {
      props: z.object({
        rows: z.array(
          z.object({
            dimensionKey: z.string(),
            dimensionName: z.string(),
            rankValue: z.number(),
            rankTotalAssets: z.number(),
            rankChange: z.number(),
          }),
        ),
        dimensionLabel: z.string().optional(),
        changeLabel: z.string().optional(),
        rankLabel: z.string().optional(),
      }),
      description: "Display a asset dimension rank change table",
    },
    AssetRatingChangeComparisonList: {
      props: z.object({
        changes: z.array(
          z.object({
            id: z.string().optional(),
            assetLogo: z.string().optional(),
            assetSymbol: z.string(),
            fromRating: z.string(),
            toRating: z.string(),
            type: jsonRenderAssetTypeZ,
          }),
        ),
      }),
      description: "Display a asset rating change comparison list",
    },
    AssetNarrativeHighlightsList: {
      props: z.object({
        items: z.array(
          z.object({
            title: z.string(),
            description: z.string(),
          }),
        ),
      }),
      description: "Display a asset narrative highlights list",
    },
    NotificationButtonList: {
      props: z.object({
        actions: z.array(
          z.object({
            type: z.enum(["negative", "positive"]),
            attributes: z.record(z.string(), z.unknown()),
          }),
        ),
      }),
      description:
        "Ordered button actions rendered in the notification footer (negative = secondary, positive = brand CTA). Wire: type + attributes per NotificationButtonList.schema.json.",
    },
    ActionButtons: {
      props: actionButtonsPropsZ,
      description:
        "Chat answer CTA row: 1–2 actions (first = primary). each navigate action opens a registered destination. Wire: ActionButtons.schema.json.",
    },

    // ─── Moves / Signal card viz (Figma 94103) ───
    AssetVolumeComparisonCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        volumeMultiplier: z.string(),
        currentVolumeRatio: z.number(),
        avgVolumeRatio: z.number(),
      }),
      description: "Volume surge card with current vs 20-day average bars",
    },
    AssetPriceSparklineCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        price: z.string(),
        priceChange: z.string(),
        priceChangePercent: z.string(),
        changeDirection: priceChangeDirectionZ,
        chartPoints: z.array(z.number()),
        annotation: chartAnnotationZ.optional(),
      }),
      description: "Price summary with mini sparkline and optional annotation",
    },
    AssetRsiGaugeCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        rsiValue: z.number(),
        indicatorPosition: z.number().optional(),
        statusTone: z.enum(["overbought", "oversold", "neutral"]).optional(),
      }),
      description: "RSI value with tri-color gauge bar",
    },
    AssetKdjGaugeCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        kdjValue: z.number(),
        indicatorPosition: z.number().optional(),
        statusTone: z.enum(["overbought", "oversold", "neutral"]).optional(),
      }),
      description: "KDJ value with tri-color gauge bar",
    },
    AssetMacdMiniChartCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        candles: z.array(ohlcCandleZ),
      }),
      description: "MACD histogram with DIF/DEA lines",
    },
    AssetEmaCandleChartCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        candles: z.array(ohlcCandleZ),
        annotation: chartAnnotationZ.optional(),
      }),
      description: "Candlestick chart with EMA7/EMA14 overlays",
    },
    AssetSentimentRatioCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        bullishCount: z.number().int().nonnegative(),
        bearishCount: z.number().int().nonnegative(),
      }),
      description: "Bullish vs bearish technical sentiment ratio bar",
    },
    AssetEarningsActualExpectedCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        metrics: z.array(earningsMetricZ),
      }),
      description: "Earnings actual vs expected with beat/miss tags",
    },
    AssetInsightSummaryCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        title: z.string(),
        bullets: z.array(z.string()).min(1),
      }),
      description: "Insight headline with bulleted highlight lines",
    },
    AssetTradePotentialCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        potentialPercent: z.string(),
        entryPrice: z.string(),
        stopLossPrice: z.string(),
        currentReturn: z.string(),
        direction: priceChangeDirectionZ.optional(),
      }),
      description: "Trade idea potential with entry/stop/current stats",
    },
    AssetSectorShiftCard: {
      props: z.object({
        ...signalCardAssetPropsZ,
        fromSectorName: z.string(),
        fromSectorChange: z.string(),
        toSectorName: z.string(),
        toSectorChange: z.string(),
      }),
      description: "Sector rotation comparison card",
    },

    // ─── Business Components (unified assetRouterId interface) ───
    AssetPriceBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description:
        "Asset price chart with time range tabs and K-line toggle. assetRouterId = asset_router_id from lookup_assets.",
    },
    ThemeHeatmapBusinessChart: {
      props: z.object({
        type: jsonRenderAssetTypeZ,
        stockType: z.enum(["US", "HK"]).optional(),
      }),
      description: "Themes heatmap",
    },
    AssetPriceForecastBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description: "Price prediction forecast chart",
    },
    AssetAnalystRatingBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description: "Analyst rating summary chart (stock only)",
    },
    AssetTechnicalSignalSummaryBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description:
        "Technical signal summary — buy/sell/neutral counts and signal level gauge",
    },
    AssetTechnicalSignalDetailBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description:
        "Technical indicator detail table — SMA, MACD, RSI, etc. with sentiment tags",
    },
    AssetHistoricalEarningsBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description:
        "Historical earnings table with period/metric switch (stock only)",
    },
    AssetDividendInfoBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description: "Dividend summary info card (stock only)",
    },
    AssetDividendHistoryBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description: "Dividend history table (stock only)",
    },
    AssetFinancialsBusinessChart: {
      props: z.object({
        assetType: jsonRenderAssetTypeZ,
        assetRouterId: z.string(),
      }),
      description:
        "Financial statements table with tab/period switch (stock only)",
    },
    WhaleHoldingChangeBusinessChart: {
      props: z.object({
        cikId: z.string(),
      }),
      description: "Whale holding change table",
    },
    FitnessScoreCard: {
      props: z.object({
        total: z.number(),
        delta: z.number(),
        dimensions: z.object({
          control: fitnessScoreDimensionSchema,
          safety: fitnessScoreDimensionSchema,
          growth: fitnessScoreDimensionSchema,
        }),
      }),
      description:
        "Financial Fitness score result card (Improved / Declined / No change)",
    },
  },
  actions: {
    export_report: { description: "Export dashboard to PDF" },
    refresh_data: { description: "Refresh all metrics" },
  },
});
